// Gebrandete Klient-Upload-Seite (G3). Drag&Drop, kein Login, KEIN
// Mandant-Dropdown — der Mandant ist an die Session gebunden (Cloud erzwingt
// ihn, ADR-023). Wiederverwendung der `bmd-upload-*`-Optik aus der
// Sachbearbeiter-UploadPage.

import { useCallback, useRef, useState } from "react";

import { Button } from "@/components/Button";

import { KlientApiError, uploadBeleg } from "./api";
import type { KlientBranding } from "./session";

import "../pages/UploadPage.css";
import "./klient.css";

interface FileResult {
  filename: string;
  ok: boolean;
  message: string;
}

interface Props {
  branding: KlientBranding | null;
}

export function KlientUploadPage({ branding }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [results, setResults] = useState<FileResult[]>([]);
  const [pending, setPending] = useState(0);
  const [expired, setExpired] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach(async (file) => {
      setPending((p) => p + 1);
      try {
        const res = await uploadBeleg(file);
        setResults((prev) => [
          {
            filename: file.name,
            ok: true,
            message: res.duplicate
              ? "bereits vorhanden (Duplikat)"
              : `${(res.file_size_bytes / 1024).toFixed(0)} KB hochgeladen`,
          },
          ...prev,
        ]);
      } catch (err) {
        if (err instanceof KlientApiError && err.status === 401) {
          setExpired(true);
        }
        setResults((prev) => [
          {
            filename: file.name,
            ok: false,
            message: err instanceof Error ? err.message : "Fehler",
          },
          ...prev,
        ]);
      } finally {
        setPending((p) => p - 1);
      }
    });
  }, []);

  if (expired) {
    return (
      <div className="klient-shell klient-center">
        <div className="klient-card klient-hinweis">
          <h1>Sitzung abgelaufen</h1>
          <p>
            Bitte öffne erneut den persönlichen Upload-Link deines
            Steuerberaters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="klient-shell">
      <header className="klient-header">
        {branding?.logo_url ? (
          <img src={branding.logo_url} alt="" className="klient-logo" />
        ) : null}
        <div className="klient-kanzlei">
          {branding?.display_name ?? "Beleg-Upload"}
        </div>
      </header>

      <main className="klient-main">
        <h1 className="klient-title">Belege hochladen</h1>
        <p className="klient-sub">
          Lade deine Belege hoch — sie gehen direkt und sicher an deine Kanzlei.
        </p>

        <div
          className={`bmd-upload-drop${
            dragOver ? " bmd-upload-drop-active" : ""
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              inputRef.current?.click();
            }
          }}
        >
          <div className="bmd-upload-mark" aria-hidden="true" />
          <div className="bmd-upload-title">
            Dateien hier ablegen oder klicken zum Auswählen
          </div>
          <div className="bmd-upload-sub">
            PDF, JPG, PNG, TIFF — max. 25&nbsp;MB pro Datei
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff"
            multiple
            hidden
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <div style={{ marginTop: 16 }}>
            <Button variant="primary" size="md">
              Datei auswählen
            </Button>
          </div>
        </div>

        {pending > 0 && <div className="bmd-upload-status">Upload läuft…</div>}

        {results.length > 0 && (
          <section className="bmd-card bmd-upload-results">
            <h3>Hochgeladen</h3>
            <ul className="bmd-upload-list">
              {results.map((r, i) => (
                <li
                  key={i}
                  className={`bmd-upload-item ${
                    r.ok ? "bmd-upload-item-ok" : "bmd-upload-item-err"
                  }`}
                >
                  <strong>{r.filename}</strong>
                  <div className="bmd-subtle" style={{ fontSize: 13 }}>
                    {r.message}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
